import { useQuery, gql, useMutation, useLazyQuery, useApolloClient } from '@apollo/client';
import Moment from 'moment';
import { compareArrayOfReasons, getCookie, getNumberWithThousandSeparator } from '../components/other/ConfigBasic';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authClient } from '../components/auth/authClient';
import { redirectToLogin, redirectToZlogin } from '../components/auth/redirect';
import axios from 'axios';
import { Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_CONTACT_PERSONS = gql`
    query getContactPersons($custNr: Object) {
        contactPersons(filter: { val: $custNr, op: EQ, key: "custNr" }, page: null, sort: { op: ASC, key: "name" }) {
            firstname
            lastname
            id
        }
    }
`;

const GET_CONTACT_COMPANIES = gql`
    query getContactCompanies($custNr: Object) {
        contactCompanies(filter: { val: $custNr, op: EQ, key: "custNr" }, page: null, sort: { op: ASC, key: "name" }) {
            legalName
            id
        }
    }
`;

export function useGetContactsApi(custNr) {
    const { data: contactPersonsData, refetch: refetchContactPersons } = useQuery(GET_CONTACT_PERSONS, {
        variables: {
            custNr,
        },
        skip: !custNr,
    });
    const { data: contactCompaniesData, refetch: refetchContactCompanies } = useQuery(GET_CONTACT_COMPANIES, {
        variables: {
            custNr,
        },
        skip: !custNr,
    });

    const refetchContacts = useCallback(async () => {
        const [contactPersonsResult, contactCompaniesResult] = await Promise.all([refetchContactPersons(), refetchContactCompanies()]);

        return {
            contactPersons: contactPersonsResult.data?.contactPersons ?? [],
            contactCompanies: contactCompaniesResult.data?.contactCompanies ?? [],
        };
    }, [refetchContactPersons, refetchContactCompanies]);

    return useMemo(() => {
        return {
            contactPersons: contactPersonsData?.contactPersons ?? [],
            contactCompanies: contactCompaniesData?.contactCompanies ?? [],
            loading: contactPersonsData === undefined || contactCompaniesData === undefined,
            refetchContacts,
        };
    }, [contactPersonsData, contactCompaniesData, refetchContacts]);
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_DRIVER = gql`
    query getDrivers {
        contactPersons(filter: { val: "true", op: EQ, key: "isDriver" }, page: null, sort: { op: ASC, key: "name" }) {
            firstname
            lastname
            id
            isDriver
            imageUri
            loginUuid
        }
    }
`;

export function useContactIsDriverApi(setMessage) {
    const { t } = useTranslation();
    const [driverData, setDriverData] = useState(undefined);

    useQuery(GET_DRIVER, {
        onCompleted: (data) => {
            let driverList = [];
            data?.contactPersons?.forEach((driver) => {
                const newDriver = {
                    firstname: driver?.firstname ? driver?.firstname : null,
                    lastname: driver?.lastname ? driver?.lastname : null,
                    id: driver?.id ? driver?.id : null,
                    isDriver: driver?.isDriver ? driver?.isDriver : null,
                    imageUri: driver?.imageUri ? driver?.imageUri : null,
                    loginUuid: driver?.loginUuid ? driver?.loginUuid : null,
                };
                driverList.push(newDriver);
            });
            if (driverList.length === 0) {
                setDriverData([]);
            } else {
                setDriverData(driverList);
            }
        },
        onError: (error) => {
            setMessage(`${t('apiErrorDriverData')} ${error?.graphQLErrors?.[0]?.message ?? error?.message}`);
            setDriverData([]);
        },
    });

    if (driverData !== undefined) {
        return driverData;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_ME = gql`
    query getMe {
        me {
            email
            custNr
            adminRoles
            loginUuid
            firstname
            lastname
            id
            zluser {
                imageUri
                org
                locked
                expiration
                permissions {
                    name
                }
            }
        }
    }
`;

const GET_LOGBOOKS = gql`
    query getLogbooks {
        logbooks {
            name
            id
            idAsset
            status
            access {
                holder
                allowed
                dtNotAfter
                dtNotBefore
            }
        }
    }
`;

/**
 * Load the current user data and a list of logbooks.
 * If user is custommer admin, the logbook list get the
 * option to create a new logbook.
 * @returns user data and logbook list
 */
export function useGetInitialData() {
    const location = useLocation();

    const { loading, error, data: userData } = useQuery(GET_ME);
    const { data: logbooks } = useQuery(GET_LOGBOOKS);
    const { data: teams } = useQuery(GET_TEAM_ACCESS);

    if (error) {
        const token = getCookie(process.env.REACT_APP_COOKIE_NAME);
        if (token.length === 0) {
            redirectToLogin(location);
        } else {
            redirectToZlogin();
        }
    }

    // Keep the returned object referentially stable between renders, the logbook list is used in effect dependencies
    return useMemo(() => {
        if (loading || !logbooks || !userData || !teams) {
            return undefined;
        }

        const logbookList = [];
        const validation = authClient(userData);

        logbooks.logbooks.forEach((logbook) => {
            let loginUserAccess = logbook.access.filter((a) => a.holder === userData.me.id || a.holder === teams?.teams[0]?.id);
            let completeAccess = [];
            loginUserAccess?.forEach((access) => {
                access?.allowed?.forEach((right) => {
                    const dtNotBeforIsValid = access?.dtNotBefore === null || access?.dtNotBefore < Moment().format('YYYY-MM-DD');
                    const dtNotAfterIsValid = access?.dtNotAfter === null || access?.dtNotAfter > Moment().format('YYYY-MM-DD');

                    if (!completeAccess.includes(right) && dtNotBeforIsValid && dtNotAfterIsValid) {
                        completeAccess.push(right);
                    }
                });
            });

            logbookList.push({
                label: logbook.name,
                value: logbook.id,
                assetId: logbook.idAsset,
                status: logbook.status,
                loginUserAccess: loginUserAccess.length > 0 ? completeAccess : null,
            });
        });
        logbookList.sort((a, b) => {
            if (a.label.toLocaleUpperCase() < b.label.toLocaleUpperCase()) {
                return -1;
            } else if (a.label.toLocaleUpperCase() > b.label.toLocaleUpperCase()) {
                return 1;
            } else {
                return 0;
            }
        });

        return {
            userData: userData,
            logbookList: logbookList,
            validation: validation,
        };
    }, [loading, logbooks, userData, teams]);
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_OPEN_TOURS = gql`
    query getOpenTours($idLogbook: Object) {
        logbooktours(filter: { op: AND, filters: [{ val: $idLogbook, op: EQ, key: "idLogbook" }, { val: "OPEN", op: EQ, key: "status" }] }) {
            dtBegin
            dtEnd
            id
            distance
            streetBegin
            postalBegin
            cityBegin
            streetEnd
            postalEnd
            cityEnd
            type
        }
    }
`;

export function useGetOpenTours(idLogbook) {
    const { data: openToursData } = useQuery(GET_OPEN_TOURS, {
        variables: {
            idLogbook,
        },
    });

    if (openToursData) {
        return openToursData.logbooktours;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_OPEN_TOURS_COUNT = gql`
    query getOpenToursCount($idLogbook: Object) {
        logbooktoursCount(filter: { op: AND, filters: [{ val: $idLogbook, op: EQ, key: "idLogbook" }, { val: "OPEN", op: EQ, key: "status" }] })
    }
`;

export function useGetOpenToursLength(idLogbook) {
    const { t } = useTranslation();
    const [getOpenToursCount, { data: openToursCountData }] = useLazyQuery(GET_OPEN_TOURS_COUNT, {
        variables: {
            idLogbook,
        },
    });

    useEffect(() => {
        if (idLogbook !== '' && idLogbook !== t('newLogbook') && idLogbook !== t('overview') && idLogbook !== t('selectLogbook')) {
            getOpenToursCount();
        }
    }, [idLogbook, getOpenToursCount, t]);

    if (idLogbook === '' || idLogbook === t('newLogbook') || idLogbook === t('overview') || idLogbook === t('selectLogbook')) {
        return 0;
    }

    if (openToursCountData) {
        return openToursCountData?.logbooktoursCount ?? 0;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const LOGBOOK_WITH_OPEN_TOURS = gql`
    query getLogbookWithOpenTours($idLogbook: Object) {
        logbooktours(page: { page: 1, pageSize: 1 }, filter: { op: AND, filters: [{ val: $idLogbook, op: EQ, key: "idLogbook" }, { val: "OPEN", op: EQ, key: "status" }] }) {
            idLogbook
        }
    }
`;

export const useGetLogbooksWithOpenTours = () => {
    const client = useApolloClient();
    const logbooksWithOpenTours = async (logbookIds) => {
        const promises = logbookIds.map((id) => {
            return client.query({
                query: LOGBOOK_WITH_OPEN_TOURS,
                variables: {
                    idLogbook: id,
                },
                context: {
                    headers: {
                        'x-customer-super': 'on',
                    },
                },
            });
        });

        try {
            const openToursResult = await Promise.all(promises);
            const filteredOpenToursResult = openToursResult.filter((result) => result.data.logbooktours.length > 0);
            return filteredOpenToursResult.map((object) => {
                return object.data.logbooktours[0].idLogbook;
            });
        } catch (error) {
            console.log(error);
            return null;
        }
    };

    return { logbooksWithOpenTours };
};

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_MODIFIED_TOURS = gql`
    query getModifiedTours($idLogbook: Object, $dtBegin: Object, $dtEnd: Object, $page: Int!, $pageSize: Int!) {
        logbooktours(
            page: { page: $page, pageSize: $pageSize }
            filter: {
                op: OR
                filters: [
                    {
                        op: AND
                        filters: [
                            { val: $idLogbook, op: EQ, key: "idLogbook" }
                            { val: "EDITED", op: EQ, key: "status" }
                            { val: $dtBegin, op: GTE, key: "dtBegin" }
                            { val: $dtEnd, op: LTE, key: "dtEnd" }
                        ]
                    }
                    {
                        op: AND
                        filters: [
                            { val: $idLogbook, op: EQ, key: "idLogbook" }
                            { val: "OUTDATED", op: EQ, key: "status" }
                            { val: $dtBegin, op: GTE, key: "dtBegin" }
                            { val: $dtEnd, op: LTE, key: "dtEnd" }
                        ]
                    }
                    {
                        op: AND
                        filters: [
                            { val: $idLogbook, op: EQ, key: "idLogbook" }
                            { val: "EXPORTED", op: EQ, key: "status" }
                            { val: $dtBegin, op: GTE, key: "dtBegin" }
                            { val: $dtEnd, op: LTE, key: "dtEnd" }
                        ]
                    }
                ]
            }
        ) {
            dtBegin
            streetBegin
            postalBegin
            cityBegin
            mileageBegin
            dtEnd
            streetEnd
            postalEnd
            cityEnd
            mileageEnd
            type
            contactName
            driver
            distance
            dtEdited
            status
            id
        }
    }
`;

export function useGetFilteredModifidedTours(idLogbook, dateFilter) {
    let dtBegin = Moment(dateFilter.dateFrom).format('YYYY-MM-DD') + 'T00:00:00.000Z';
    let dtEnd = Moment(dateFilter.dateUntil).format('YYYY-MM-DD') + 'T23:59:59.999Z';
    const [modifiedTours, setModifiedTours] = useState([]);
    const [page, setPage] = useState(1);
    const pageSize = 1000;

    const { loading } = useQuery(GET_MODIFIED_TOURS, {
        variables: {
            idLogbook,
            dtBegin,
            dtEnd,
            page,
            pageSize,
        },
        onCompleted: (data) => {
            setModifiedTours((prev) => {
                const merged = [...prev, ...data.logbooktours];
                if (data.logbooktours.length === pageSize) {
                    setPage((p) => p + 1);
                }
                return merged;
            });
        },
    });

    useEffect(() => {
        setModifiedTours([]);
        setPage(1);
    }, [idLogbook, dtBegin, dtEnd]);

    return loading ? [] : modifiedTours;
}


//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

//--------------------------------------------------//
//------check validation for export-----------------//
//--------------------------------------------------//

const CHECK_EXPORT_VALIDATIONS = gql`
    query getOpenToursInTimeRange($idLogbook: Object, $dtBegin: Object, $dtEnd: Object) {
        logbooktours(
            filter: {
                op: AND
                filters: [
                    { val: $idLogbook, op: EQ, key: "idLogbook" }
                    { val: "OPEN", op: EQ, key: "status" }
                    { val: $dtBegin, op: GTE, key: "dtBegin" }
                    { val: $dtEnd, op: LTE, key: "dtEnd" }
                ]
            }
        ) {
            dtBegin
            status
            type
            dtEdited
            dtEnd
            id
        }
    }
`;

export function useCheckExportValidations(idLogbook, dateFilter) {
    let dtBegin = Moment(dateFilter.dateFrom).format('YYYY-MM-DD') + 'T00:00:00.000Z';
    let dtEnd = Moment(dateFilter.dateUntil).format('YYYY-MM-DD') + 'T23:59:59.999Z';
    const { data: timeRangeToursData } = useQuery(CHECK_EXPORT_VALIDATIONS, {
        fetchPolicy: 'no-cache',
        variables: {
            idLogbook,
            dtBegin,
            dtEnd,
        },
    });

    if (timeRangeToursData) {
        if (timeRangeToursData.logbooktours.length > 0) {
            return false;
        } else {
            return true;
        }
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_SELECTED_TOUR = gql`
    query logbooktours($id: Object) {
        logbooktours(filter: { val: $id, op: EQ, key: "id" }) {
            access {
                allowed
                dtNotAfter
                dtNotBefore
                holder
                variant
            }
            cityBegin
            cityEnd
            countryBegin
            countryEnd
            custNr
            dtBegin
            dtCreated
            dtEnd
            id
            idLogbook
            latlonBegin
            latlonEnd
            distance
            mileageBegin
            mileageEnd
            postalBegin
            postalEnd
            stateBegin
            stateEnd
            streetBegin
            streetEnd
            driver
            type
            reason
            contactName
            costCentre
            costObject
            annotation
            status
            calibrationType
        }
    }
`;

export function useGetSelectedTour(id) {
    const { data: selectedTour } = useQuery(GET_SELECTED_TOUR, {
        variables: {
            id,
        },
        skip: !id,
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-first',
        onError: (error) => {
            console.log(error?.graphQLErrors?.[0]?.message ?? error?.message);
        },
    });

    return useMemo(() => selectedTour?.logbooktours, [selectedTour]);
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_SELECTED_MIXED_TOUR = gql`
    query getSelectedMixedTour($id: Object) {
        logbooktours(filter: { val: $id, op: EQ, key: "id" }) {
            streetBegin
            postalBegin
            cityBegin
            streetEnd
            postalEnd
            cityEnd
        }
    }
`;

export function useGetSelectedMixedTour(id, setMessage) {
    const [getTourData, { data: selectedTour }] = useLazyQuery(GET_SELECTED_MIXED_TOUR, {
        variables: {
            id,
        },
        onError: (error) => {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
        },
    });

    useEffect(() => {
        getTourData();
    }, [id, getTourData]);

    if (selectedTour) {
        return selectedTour.logbooktours;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_SELECTED_TOUR_STATUS = gql`
    query getSelectedTourStatus($id: Object) {
        logbooktours(filter: { val: $id, op: EQ, key: "id" }) {
            status
        }
    }
`;

export function useGetSelectedTourStatus(id) {
    const [getTourStatus, { data: selectedTour }] = useLazyQuery(GET_SELECTED_TOUR_STATUS, {
        variables: {
            id,
        },
    });

    useEffect(() => {
        if (id !== undefined) {
            getTourStatus();
        }
    }, [getTourStatus, id]);

    if (id === undefined) {
        return false;
    }

    if (selectedTour && selectedTour.logbooktours.length > 0) {
        return selectedTour.logbooktours[0].status === 'OPEN' ? false : true;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_ASSETS = gql`
    query GetAssets($custNr: Object) {
        assets(sort: null, page: null, filter: { op: AND, filters: [{ val: $custNr, op: EQ, key: "custNr" }, { val: null, op: NE, key: "child" }] }) {
            name
            id
            child {
                __typename
            }
        }
    }
`;

const filterVehicleData = (assetData) => {
    return assetData.child.__typename === 'VehicleChild';
};

export function useGetVehiclesApi(custNr) {
    const { data: assetsData } = useQuery(GET_ASSETS, {
        variables: {
            custNr,
        },
    });

    if (assetsData) {
        let filteredVehiclesData = assetsData.assets.filter(filterVehicleData);
        let sortedVehicleData = filteredVehiclesData.sort((a, b) => {
            if (a.name.toLocaleUpperCase() < b.name.toLocaleUpperCase()) {
                return -1;
            } else if (a.name.toLocaleUpperCase() > b.name.toLocaleUpperCase()) {
                return 1;
            } else {
                return 0;
            }
        });
        return sortedVehicleData;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_LOGBOOK_DRIVERS = gql`
    query getLogbookDrivers($id: Object) {
        logbooks(filter: { val: $id, op: EQ, key: "id" }) {
            idDrivers
        }
    }
`;

export function useGetLogbookDriversApi(logbookId) {
    const { data: driverIds } = useQuery(GET_LOGBOOK_DRIVERS, {
        variables: {
            id: logbookId,
        },
    });
    if (driverIds !== undefined) {
        return driverIds?.logbooks[0]?.idDrivers;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

export const UPDATE_MILEAGE = gql`
    mutation updateMileage($id: ID!, $km: Float!) {
        updateAssetOdom(id: $id, km: $km)
    }
`;

export const parseMileage = (value) => {
    if (value === '' || value === null || value === undefined) {
        return null;
    }

    const mileage = Number(value);
    return Number.isFinite(mileage) && mileage > 0 ? mileage : null;
};

export const getLogbookErrorMessage = (error, fallbackMessage) => {
    const graphQLError = error?.graphQLErrors?.[0];
    const detailDescription = graphQLError?.extensions?.detailDescription || '';

    if (detailDescription.includes('E11000') && detailDescription.includes('idAsset')) {
        return 'LOGBOOK_ALREADY_EXISTS';
    }

    return graphQLError?.message || error?.message || fallbackMessage;
};

const CREATE_NEW_LOGBOOK = gql`
    mutation createNewLogbook($idAsset: String!, $idDrivers: [String!], $name: String!, $status: EnumLogbookStatus!) {
        createLogbook(input: { idAsset: $idAsset, idDrivers: $idDrivers, name: $name, status: $status }) {
            name
            id
            status
        }
    }
`;

export function CreateNewLogbook(props) {
    const { t } = useTranslation();
    const parsedMileage = parseMileage(props.createLogbook.mileage);
    const [updateMileage] = useMutation(UPDATE_MILEAGE, {
        variables: {
            id: props.createLogbook.idAsset,
            km: parsedMileage,
        },
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
        onCompleted: () => {
            createLogbook();
        },
        onError: (error) => {
            const message = getLogbookErrorMessage(error, t('logbookCreationFailed'));
            props.setMessage(message === 'LOGBOOK_ALREADY_EXISTS' ? t('logbookAlreadyExists') : message);
        },
    });

    const [createLogbook] = useMutation(CREATE_NEW_LOGBOOK, {
        variables: {
            idAsset: props.createLogbook.idAsset,
            idDrivers: props.createLogbook.idDrivers,
            name: props.createLogbook.name,
            status: props.createLogbook.mileage === '' ? 'pending' : 'active',
        },
        refetchQueries: [{ query: GET_LOGBOOKS }],
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
        onCompleted: () => {
            props.resetForm();
            props.setMessage(t('logbookSuccessfulCreated'));
        },
        onError: (error) => {
            const message = getLogbookErrorMessage(error, t('logbookCreationFailed'));
            props.setMessage(message === 'LOGBOOK_ALREADY_EXISTS' ? t('logbookAlreadyExists') : message);
        },
    });

    const createNewLogbook = () => {
        const createLogbookFormIsValid = props.formValidation(props.setValidationErrors, props.createLogbook.idAsset, props.createLogbook.idDrivers, props.createLogbook.name);
        if (createLogbookFormIsValid) {
            if (props.createLogbook.mileage !== '' && parsedMileage === null) {
                props.setMessage(t('invalidMileage'));
            } else if (parsedMileage !== null) {
                updateMileage();
            } else {
                createLogbook();
            }
        }
    };

    return (
        <>
            <Box textTransform={'uppercase'} sx={{ cursor: 'pointer' }} onClick={() => createNewLogbook()}>
                {t('create')}
            </Box>
        </>
    );
}

//--------------------------------------------------//
//--------------- Create Logbooks-------------------//
//--------------------------------------------------//

const CREATE_LOG_BOOKS = gql`
    mutation CreateLogbook($name: String!, $idAsset: String!, $idDrivers: [String!], $status: EnumLogbookStatus!, $access: [AccessInput]) {
        createLogbook(input: { access: $access, idAsset: $idAsset, idDrivers: $idDrivers, name: $name, status: $status }) {
            id
            name
            status
        }
    }
`;

export const useCreateLogbooks = (setMessage) => {
    const { t } = useTranslation();
    const [createLogbook, { loading: createLogbookLoading, error: createLogbookError }] = useMutation(CREATE_LOG_BOOKS, {
        refetchQueries: [{ query: GET_LOGBOOKS }],
        onError: (error) => {
            const message = getLogbookErrorMessage(error, t('logbookCreationFailed'));
            setMessage(message === 'LOGBOOK_ALREADY_EXISTS' ? t('logbookAlreadyExists') : message);
        },
    });
    const [updateMileage, { loading: updateMileageLoading, error: updateMileageError }] = useMutation(UPDATE_MILEAGE);

    const createLogbookItem = async (name, idAsset, idDrivers, access, km, id) => {
        const parsedKm = parseMileage(km);
        const hasMileage = km !== '' && km !== null && km !== undefined;

        if (hasMileage && parsedKm === null) {
            setMessage(t('invalidMileage'));
            return;
        }

        try {
            if (parsedKm !== null) {
                await updateMileage({
                    variables: {
                        id,
                        km: parsedKm,
                    },
                });
            }

            await createLogbook({
                variables: { name, idAsset, idDrivers, status: parsedKm === null ? 'pending' : 'active', access },
            });

            setMessage('active');
        } catch (error) {
            const message = getLogbookErrorMessage(error, t('logbookCreationFailed'));
            setMessage(message === 'LOGBOOK_ALREADY_EXISTS' ? t('logbookAlreadyExists') : message);
        }
    };

    return { createLogbookItem, loading: createLogbookLoading || updateMileageLoading, error: createLogbookError || updateMileageError };
};

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_ALL_TOUR_VERSIONS = gql`
    query getAllTourVersions($idLogbook: Object, $dtBegin: Object) {
        logbooktours(
            sort: { key: "dtEdited", op: DESC }
            filter: { op: AND, filters: [{ val: $idLogbook, op: EQ, key: "idLogbook" }, { val: $dtBegin, op: EQ, key: "dtBegin" }] }
        ) {
            id
            dtEdited
            idEditor
        }
    }
`;

export function useGetAllTourVersionsApi(idLogbook, dtBegin) {
    const { data: allTourVersions } = useQuery(GET_ALL_TOUR_VERSIONS, {
        variables: {
            idLogbook: idLogbook,
            dtBegin: dtBegin,
        },
    });
    if (allTourVersions !== undefined) {
        return allTourVersions.logbooktours;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_SELECTED_LOGBOOK = gql`
    query getSelectedLogbook($id: Object) {
        logbooks(filter: { val: $id, op: EQ, key: "id" }) {
            idCreated
            dtCreated
            id
            idAsset
            idDrivers
            name
            access {
                holder
                allowed
                variant
                dtNotBefore
                dtNotAfter
            }
            notifications {
                topic
                receivers
            }
        }
    }
`;

export function useGetSelectedLogbookApi(idLogbook) {
    const { data: selectedLogbook } = useQuery(GET_SELECTED_LOGBOOK, {
        variables: {
            id: idLogbook,
        },
    });

    if (selectedLogbook !== undefined) {
        return selectedLogbook.logbooks[0];
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_ASSET_BASE_DATA = gql`
    query getAssetBaseData($id: Object) {
        assets(filter: { val: $id, op: EQ, key: "id" }) {
            id
            name
            child {
                ... on VehicleChild {
                    imageUri
                    licensePlate
                    vin
                    model
                    make
                }
            }
        }
    }
`;

export function useGetAssetDataApi(idAsset, setMessage = console.log) {
    const [assetData, setAssetData] = useState({
        imageUri: undefined,
        licensePlate: undefined,
        make: undefined,
        model: undefined,
        vin: undefined,
        name: undefined,
    });

    useQuery(GET_ASSET_BASE_DATA, {
        variables: {
            id: idAsset,
        },
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
        onCompleted: (data) => {
            const asset = data?.assets[0]?.child;
            setAssetData({
                imageUri: asset?.imageUri ? asset?.imageUri : null,
                licensePlate: asset?.licensePlate ? asset?.licensePlate : null,
                make: asset?.make ? asset?.make : null,
                model: asset?.model ? asset?.model : null,
                vin: asset?.vin ? asset?.vin : null,
                name: data?.assets[0]?.name ? data?.assets[0]?.name : null,
            });
        },
        onError: (error) => {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
            setAssetData({
                imageUri: null,
                licensePlate: null,
                make: null,
                model: null,
                vin: null,
                name: null,
            });
        },
    });

    if (assetData.vin !== undefined) {
        return assetData;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

// currentDriver is the backend-resolved answer to "who is driving this asset?"
// (IC-811). It replaces the client-side scan of driverRegistration, which only ever
// saw software registrations and disagreed with the backend about which open
// registration wins. Selecting the field is what makes minx resolve it at all, so
// this query is the only place in the app that pays for it.
const GET_ASSET_DATA_FOR_STATUS = gql`
    query getAssetDataForStatus($idAsset: Object) {
        assets(filter: { val: $idAsset, op: EQ, key: "id" }) {
            logLast {
                fuelPercent
                latlng
            }
            currentDriver {
                displayName
                source
                driver {
                    id
                    firstname
                    lastname
                }
                software {
                    active
                    stale
                    registration {
                        idContactPerson
                        start
                        end
                    }
                }
                hardware {
                    active
                    unresolved
                }
                conflict {
                    exists
                    reason
                }
            }
        }
    }
`;

export function useGetAssetDataForStatus(idAsset, setMessage, fetchPolicy) {
    const { data } = useQuery(GET_ASSET_DATA_FOR_STATUS, {
        variables: {
            idAsset,
        },
        fetchPolicy,
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
        onError: (error) => {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
        },
    });

    if (data !== undefined) {
        if (data.assets.length === 0) {
            //No Access to asset
            return [];
        } else {
            return data.assets[0];
        }
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_ASSET_LOGS = gql`
    query getAssetLogs($id: String!, $startDate: DateTime!, $endDate: DateTime!) {
        assetLogs(start: $startDate, end: $endDate, idAsset: $id) {
            speed
            latlng
            date
            why
            heading
        }
    }
`;

export function useGetAssetLogs(idAsset, tourId, setMessage) {
    let dataMissing = true;
    const originTourData = useGetSelectedTour(tourId);
    const startDate = originTourData ? Moment(originTourData[0]?.dtBegin) : undefined;
    const endDate = originTourData ? Moment(originTourData[0]?.dtEnd) : undefined;

    if (startDate && endDate) {
        dataMissing = false;
    }

    const { data: assetLogs } = useQuery(GET_ASSET_LOGS, {
        skip: dataMissing,
        variables: {
            id: idAsset,
            startDate: startDate,
            endDate: endDate,
        },

        onError: (error) => {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
        },
    });
    if (assetLogs) {
        return {
            dayLogs: assetLogs?.assetLogs,
            startDate: Moment(originTourData[0].dtBegin),
            endDate: Moment(originTourData[0].dtEnd),
        };
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_PERSONS = gql`
    query getPersons {
        persons {
            firstname
            lastname
            id
            zluser {
                id
                permissions {
                    name
                }
            }
        }
    }
`;

const GET_PERSONS_ADMIN = gql`
    query getPersons {
        persons {
            firstname
            lastname
            id
            access {
                holder
                allowed
                variant
            }
            zluser {
                id
                permissions {
                    name
                }
            }
        }
    }
`;

export function useGetPersonsApi() {
    const { data: persons } = useQuery(GET_PERSONS);

    if (persons !== undefined) {
        return persons.persons;
    }
}

export function useGetPersonsAdminApi() {
    const { data: persons } = useQuery(GET_PERSONS_ADMIN, {
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
    });
    if (persons !== undefined) {
        return persons.persons;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_FILTERED_TOURS = gql`
    query getFilteredTours($idLogbook: Object, $dtBegin: Object, $dtEnd: Object) {
        logbooktours(
            filter: { op: AND, filters: [{ val: $idLogbook, op: EQ, key: "idLogbook" }, { val: $dtBegin, op: GTE, key: "dtBegin" }, { val: $dtEnd, op: LTE, key: "dtEnd" }] }
        ) {
            id
            dtBegin
            dtEnd
        }
    }
`;

export function useGetFilteredTours(idLogbook, dtBegin, dtEnd) {
    const { data: filteredToursData } = useQuery(GET_FILTERED_TOURS, {
        variables: {
            idLogbook,
            dtBegin,
            dtEnd,
        },
    });

    if (filteredToursData) {
        return filteredToursData.logbooktours;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

export const GET_SELECTED_LOGBOOK_REASONS = gql`
    query getSelectedLogbookReasons($id: Object) {
        logbooks(filter: { val: $id, op: EQ, key: "id" }) {
            id
            reasons {
                label
                idPerson
            }
        }
    }
`;

export function useGetSelectedLogbookReasons(idLogbook, setMessage, setReasons, currentUserId) {
    useQuery(GET_SELECTED_LOGBOOK_REASONS, {
        variables: {
            id: idLogbook,
        },
        skip: !idLogbook,
        onCompleted: (data) => {
            const logbook = data?.logbooks?.[0];

            if (logbook === undefined) {
                return;
            }

            let loadedReasons = (logbook.reasons ?? [])
                .filter((reason) => reason?.label)
                .map((reason) => {
                    return {
                        label: reason.label,
                        idPerson: reason.idPerson ?? null,
                    };
                });

            const uniqueReasons = loadedReasons.reduce((accumulator, current) => {
                if (!accumulator.find((item) => item.label === current.label && item.idPerson === current.idPerson)) {
                    accumulator.push(current);
                }
                return accumulator;
            }, []);

            const filteredReasons = uniqueReasons.filter((reason) => reason.idPerson === null || reason.idPerson === currentUserId);

            setReasons(filteredReasons.sort(compareArrayOfReasons));
        },
        onError: (error) => {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
        },
    });
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

export const GET_ASSET_REAL_ODOM = gql`
    query getAssetRealOdom($id: Object) {
        assets(filter: { val: $id, op: EQ, key: "id" }) {
            logLast {
                realOdom
            }
        }
    }
`;

export function useGetAssetRealOdom(idAsset, callAgain, callback, setMessage) {
    const [getRealOdom] = useLazyQuery(GET_ASSET_REAL_ODOM, {
        variables: {
            id: idAsset === '' ? null : idAsset,
        },
        onCompleted: (data) => {
            const realOdom = data?.assets[0]?.logLast?.realOdom;
            if (realOdom) {
                callback((realOdom / 1000).toFixed(0));
            } else {
                callback('');
            }
        },
        onError: (error) => {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
        },
    });

    useEffect(() => {
        if (callAgain) {
            getRealOdom();
        }
    }, [callAgain, getRealOdom]);
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_LOGBOOK_PRIVATE_JOURNEY = gql`
    query getLogbookPrivateJourney($id: ID!) {
        getLogbookPrivateJourney(id: $id) {
            idPrivateJourneyPerson
        }
    }
`;

export function useGetLogbookPrivateJourney(idLogbook, setMessage, callback) {
    useQuery(GET_LOGBOOK_PRIVATE_JOURNEY, {
        variables: {
            id: idLogbook,
        },
        skip: !idLogbook,
        onCompleted: (data) => {
            callback(data?.getLogbookPrivateJourney?.idPrivateJourneyPerson);
        },
        onError: (error) => {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
        },
    });
}

/*
 //Eine Query in Abhängigkeit einer anderen Query aufrufen
 export function useContactApiTest() {
     const { data: userData } = useQuery(GET_ME)
     const custNr = userData?.me?.custNr
     const { data: contactsData } = useQuery(GET_CONTACTS, {
         skip: !custNr,
         variables: {
             custNr
         }
     })
 
     if (contactsData !== undefined) {
         return contactsData;
     }
 }
 
 //LazyQuery um eine Query in Abhängigkeit einer Aktion aufzurufen
 export function ContactApi(custNr) {
     const [getContactPersons, { error, loading, data, called }] = useLazyQuery(GET_CONTACTS,  {
         variables: {
             custNr,
         },
     });
 
     useEffect(() => {
         getContactPersons();
     }, [getContactPersons]);
 
     console.log({
         called,
         error,
         loading,
         data
     })
 
     return {
         data
     };
 };
 */

/*
 export function useMeApi() {
     const [getCurrentUser, { error, loading, data, called }] = useLazyQuery(GET_ME);
 
     useEffect(() => {
     getCurrentUser();
     }, [getCurrentUser]);
 
     console.log({
         called,
         error,
         loading,
         data
       })
 
     if (data) {
         return data.me.custNr;
     }
 }
 */

const GET_BATTERY_LEVEL = gql`
    query battery($assetIds: [String]!, $dtBegin: DateTime!, $dtEnd: DateTime!) {
        batteryLevelReportData(startDateTime: $dtBegin, assetIds: $assetIds, endDateTime: $dtEnd) {
            address
            assetName
            isOldReading
            level
            oldReadingTime
            serial
            time
        }
    }
`;

export function useGetBatteryLevel(assetIds, dtBegin, dtEnd) {
    const { data: filterdBatterLevel } = useQuery(GET_BATTERY_LEVEL, {
        variables: {
            assetIds,
            dtBegin,
            dtEnd,
        },
    });

    if (filterdBatterLevel) {
        return filterdBatterLevel;
    }
}

//

const GET_LICENSE = gql`
    query getLicense {
        me {
            zluser {
                permissions {
                    name
                }
            }
        }
    }
`;

export function useGetLicenses() {
    const { loading, data: userData } = useQuery(GET_LICENSE);
    if (!loading) {
        return userData.me.zluser.permissions;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_ALL_RECEIPTS = gql`
    query receipts($id: Object, $firstDayOfMonth: Object, $lastDayOfMonth: Object) {
        logbookReceipts(
            filter: {
                op: AND
                filters: [{ key: "idLogbook", val: $id, op: EQ }, { val: $firstDayOfMonth, op: GT, key: "dtBilling" }, { val: $lastDayOfMonth, op: LT, key: "dtBilling" }]
            }
        ) {
            access {
                allowed
                dtNotAfter
                dtNotBefore
                holder
                variant
            }
            address {
                city
                coordinates
                country
                houseNo
                street
                zip
            }
            articles {
                category
                quantity
                total
                type
                unit
                unitPrice
                vat
            }
            currency
            custNr
            dtBilling
            dtCreated
            id
            idCreator
            idLogbook
            imageUrl
            netTotal
            number
            total
            vendor
        }
    }
`;

export function useGetReceipts(id, datum) {
    const firstDayOfMonth = Moment(datum).startOf('month');
    const lastDayOfMonth = Moment(datum).endOf('month');
    const {
        data: receipts,
        loading,
        error,
    } = useQuery(GET_ALL_RECEIPTS, {
        variables: {
            id: id,
            firstDayOfMonth: firstDayOfMonth,
            lastDayOfMonth: lastDayOfMonth,
        },
    });

    return { receipts, loading, error };
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_ALL_COST_RECEIPTS = gql`
    query receipts($firstDayOfMonth: Object, $lastDayOfMonth: Object) {
        logbookReceipts(filter: { op: AND, filters: [{ val: $firstDayOfMonth, op: GT, key: "dtBilling" }, { val: $lastDayOfMonth, op: LT, key: "dtBilling" }] }) {
            access {
                allowed
                dtNotAfter
                dtNotBefore
                holder
                variant
            }
            address {
                city
                coordinates
                country
                houseNo
                street
                zip
            }
            articles {
                category
                quantity
                total
                type
                unit
                unitPrice
                vat
                costCentre
                costObject
            }
            currency
            custNr
            dtBilling
            dtCreated
            id
            idCreator
            idLogbook
            imageUrl
            netTotal
            number
            total
            vendor
        }
    }
`;

export function useGetAllReceipts(datum) {
    const firstDayOfMonth = Moment(datum).startOf('month');
    const lastDayOfMonth = Moment(datum).endOf('month');
    const { data: receipts } = useQuery(GET_ALL_COST_RECEIPTS, {
        variables: {
            firstDayOfMonth: firstDayOfMonth,
            lastDayOfMonth: lastDayOfMonth,
        },
    });
    return { receipts };
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_ONE_RECEIPT = gql`
    query receipt($id: Object) {
        logbookReceipts(filter: { key: "id", val: $id, op: EQ }) {
            address {
                city
                coordinates
                country
                houseNo
                street
                zip
            }
            articles {
                category
                quantity
                total
                type
                unit
                unitPrice
                vat
                costCentre
                costObject
            }
            dtBilling
            id
            imageUrl
            netTotal
            number
            total
            vendor
        }
    }
`;

export function useGetReceipt(id) {
    const {
        data: receipt,
        loading,
        error,
    } = useQuery(GET_ONE_RECEIPT, {
        variables: {
            id: id,
        },
    });
    return { receipt, loading, error };
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const DELETE_ONE_RECEIPT = gql`
    mutation deleteReceipt($id: ID!) {
        deleteLogbookReceipt(id: $id) {
            affectedRows
        }
    }
`;

export const useDeleteReceipt = (setMessage) => {
    const { t } = useTranslation();
    const client = useApolloClient();
    const location = useLocation();

    const [deleteReceipt] = useMutation(DELETE_ONE_RECEIPT, {
        onCompleted: () => {
            setMessage(t('receiptDeleted'));
            if (location.pathname.includes('/costsOverview')) {
                client.refetchQueries({
                    include: ['receipts'],
                });
            }
        },
        onError: () => {
            setMessage(t('receiptDeletedFehler'));
        },
    });

    return { deleteReceipt };
};

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const CREATE_LOGBOOK_RECEIPT = gql`
    mutation cReceipt(
        $address: LogbookReceiptAddressInput
        $articles: [LogbookReceiptArticleInput]!
        $currency: EnumLogbookReceiptCurrency!
        $dtBilling: DateTime!
        $netTotal: Float!
        $number: String!
        $total: Float!
        $vendor: String!
        $idLogbook: String!
    ) {
        createLogbookReceipt(
            input: { address: $address, articles: $articles, currency: $currency, dtBilling: $dtBilling, netTotal: $netTotal, number: $number, total: $total, vendor: $vendor }
            idLogbook: $idLogbook
        ) {
            total
            id
            netTotal
        }
    }
`;

export function useCreateReceipt(setMessage) {
    const { t } = useTranslation();
    let navigate = useNavigate();
    const [createLogbookReceipt, { loading }] = useMutation(CREATE_LOGBOOK_RECEIPT);

    const createReceipt = async (
        address,
        currency,
        dtBilling,
        netTotal,
        number,
        total,
        vendor,
        idLogbook,
        quantity,
        type,
        unit,
        unitPrice,
        vat,
        file,
        category,
        costCentre,
        costObject
    ) => {
        let errorOccurred = false;
        let idLogbookReceipt = '';
        let logbookReceiptArticle = [];

        if (type === '') {
            logbookReceiptArticle.push({
                category: category,
                quantity: quantity,
                total: total,
                unit: unit,
                unitPrice: unitPrice,
                vat: vat,
                costCentre: costCentre,
                costObject: costObject,
            });
        } else {
            logbookReceiptArticle.push({
                category: category,
                quantity: quantity,
                total: total,
                type: type,
                unit: unit,
                unitPrice: unitPrice,
                vat: vat,
                costCentre: costCentre,
                costObject: costObject,
            });
        }

        try {
            const receiptResponse = await createLogbookReceipt({
                variables: {
                    address: address,
                    articles: logbookReceiptArticle,
                    currency: currency,
                    dtBilling: dtBilling,
                    netTotal: netTotal,
                    number: number,
                    total: total,
                    vendor: vendor,
                    idLogbook: idLogbook,
                },
            });
            idLogbookReceipt = receiptResponse.data.createLogbookReceipt.id;

            if (file) {
                await axios
                    .post(`${process.env.REACT_APP_MINX_API_URL}/${idLogbookReceipt}`, file, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            Authorization: `Bearer ${getCookie(process.env.REACT_APP_COOKIE_NAME)}`,
                        },
                    })
                    .catch((error) => {
                        errorOccurred = true;
                        console.log(error);
                        navigate(`/costs/${idLogbookReceipt}`, {
                            state: {
                                snackbarMessage: t('fehlerImage'),
                            },
                        });
                    });
            }
        } catch (error) {
            errorOccurred = true;
            console.log(error);
            setMessage(t('receiptCreationError'));
        }

        if (!errorOccurred) {
            navigate('/costsOverview', {
                state: {
                    snackbarMessage: t('newReceiptCreated'),
                },
            });
        }
    };

    return { createReceipt, loading };
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const UPDATE_RECEIPT = gql`
    mutation updateReceipt(
        $address: LogbookReceiptAddressInput
        $articles: [LogbookReceiptArticleInput]!
        $currency: EnumLogbookReceiptCurrency!
        $dtBilling: DateTime!
        $netTotal: Float!
        $number: String!
        $total: Float!
        $vendor: String!
        $id: ID!
    ) {
        updateLogbookReceipt(
            input: { address: $address, articles: $articles, currency: $currency, dtBilling: $dtBilling, netTotal: $netTotal, number: $number, total: $total, vendor: $vendor }
            id: $id
        ) {
            address {
                city
                coordinates
                country
                houseNo
                street
                zip
            }
            articles {
                category
                quantity
                total
                type
                unit
                unitPrice
                vat
                costCentre
                costObject
            }
            currency
            custNr
            dtBilling
            dtCreated
            id
            idCreator
            idLogbook
            imageUrl
            netTotal
            number
            total
            vendor
        }
    }
`;

export function useUpdateReceipt(setMessage) {
    const { t } = useTranslation();
    let navigate = useNavigate();
    const [updateLogbookReceipt] = useMutation(UPDATE_RECEIPT);

    const updateReceipt = async (
        address,
        currency,
        dtBilling,
        netTotal,
        number,
        total,
        vendor,
        id,
        quantity,
        type,
        unit,
        unitPrice,
        vat,
        file,
        category,
        costCentre,
        costObject
    ) => {
        let errorOccurred = false;
        let logbookReceiptArticle = [];

        if (type === '') {
            logbookReceiptArticle.push({
                category: category,
                quantity: quantity,
                total: total,
                unit: unit,
                unitPrice: unitPrice,
                vat: vat,
                costCentre: costCentre,
                costObject: costObject,
            });
        } else {
            logbookReceiptArticle.push({
                category: category,
                quantity: quantity,
                total: total,
                type: type,
                unit: unit,
                unitPrice: unitPrice,
                vat: vat,
                costCentre: costCentre,
                costObject: costObject,
            });
        }

        try {
            await updateLogbookReceipt({
                variables: {
                    address: address,
                    articles: logbookReceiptArticle,
                    currency: currency,
                    dtBilling: dtBilling,
                    netTotal: netTotal,
                    number: number,
                    total: total,
                    vendor: vendor,
                    id: id,
                },
            });

            if (file !== null) {
                await axios
                    .post(`${process.env.REACT_APP_MINX_API_URL}/${id}`, file, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            Authorization: `Bearer ${getCookie(process.env.REACT_APP_COOKIE_NAME)}`,
                        },
                    })
                    .catch((error) => {
                        errorOccurred = true;
                        console.log(error);
                        setMessage(t('imageUpdate'));
                    });
            }
        } catch (e) {
            errorOccurred = true;
            console.log(e);
            setMessage(e?.graphQLErrors?.[0]?.message ?? e?.message);
        }

        if (!errorOccurred) {
            navigate('/costsOverview', {
                state: {
                    snackbarMessage: t('receiptUpdated'),
                },
            });
        }
    };
    return { updateReceipt };
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_LOGBOOK_APPSETTINGS = gql`
    query appsettings($idPerson: Object) {
        appsettings(filter: { op: AND, filters: [{ val: $idPerson, op: EQ, key: "idPerson" }, { val: "logbook", op: EQ, key: "app" }] }) {
            setting {
                settingId
                settingValue
            }
            id
        }
    }
`;

const CREATE_LOGBOOK_APPSETTING = gql`
    mutation createAppSetting($idPerson: String!) {
        createAppSetting(
            input: { app: "logbook", idPerson: $idPerson, setting: [{ settingId: "onboarding", settingValue: "false" }, { settingId: "lastLogbook", settingValue: null }] }
        ) {
            setting {
                settingId
                settingValue
            }
            id
        }
    }
`;

export function useGetAppSettings(idPerson, setMessage, checkAppSetting = false) {
    const { data } = useQuery(GET_LOGBOOK_APPSETTINGS, {
        variables: { idPerson },
        onError: (error) => {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
        },
    });

    const [createAppSetting, { data: appSettingsData, called }] = useMutation(CREATE_LOGBOOK_APPSETTING, {
        variables: {
            idPerson: idPerson,
        },
        onError: (error) => {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
        },
    });

    useEffect(() => {
        if (checkAppSetting && !called && data && data?.appsettings?.length === 0) {
            createAppSetting();
        }
    }, [data, createAppSetting, called, checkAppSetting]);

    if (data?.appsettings?.length > 0) {
        return data;
    } else if (appSettingsData?.createAppSetting?.setting?.length > 0) {
        const newData = {
            appsettings: [
                {
                    id: appSettingsData?.createAppSetting?.id,
                    setting: appSettingsData?.createAppSetting?.setting,
                },
            ],
        };
        return newData;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const UPDATE_LOGBOOK_APPSETTING = gql`
    mutation updateLogbookAppSetting($id: ID!, $updatedSettingEntry: [AppSettingEntryInput]!) {
        updateAppSetting(input: { setting: $updatedSettingEntry }, id: $id) {
            app
            custNr
            id
            idPerson
            setting {
                settingId
                settingValue
            }
            tags
        }
    }
`;

export const useUpdateAppSettings = (idPerson, setMessage) => {
    const { data } = useQuery(GET_LOGBOOK_APPSETTINGS, {
        variables: { idPerson },
        onError: (error) => {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
        },
    });

    const [updateAppSettingMutation, { loading, error }] = useMutation(UPDATE_LOGBOOK_APPSETTING, {
        refetchQueries: [{ query: GET_LOGBOOK_APPSETTINGS }],
    });

    const updateAppSettings = async (settingEntry) => {
        let settingEntryNotExist = true;
        const updatedSettingEntry = data?.appsettings[0]?.setting?.map((sE) => {
            if (sE.settingId === settingEntry.settingId) {
                settingEntryNotExist = false;
                return settingEntry;
            } else {
                return {
                    settingId: sE.settingId,
                    settingValue: sE.settingValue,
                };
            }
        });
        if (settingEntryNotExist) {
            //To clean up old appSettings data
            updatedSettingEntry.push(settingEntry);
        }
        const id = data?.appsettings[0]?.id;

        try {
            await updateAppSettingMutation({ variables: { id, updatedSettingEntry } });
        } catch (err) {
            return err;
        }
    };

    return { updateAppSettings, loading, error };
};

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

// Driver login. Still on the legacy logbook route on purpose: it is the only write
// path that verifies the caller is a driver *of this logbook*, which the newer
// asset-driver route does not check. Migrating it would widen who may register
// (IC-961), so it stays until a follow-up closes that gap.
const UPDATE_DRIVER_REGISTRATION = gql`
    mutation updateDriverRegistration($id: ID!, $register: Boolean!) {
        updateLogbookDriverRegistration(id: $id, register: $register) {
            idContactPerson
        }
    }
`;

export const useUpdateDriverRegistration = () => {
    const [updateDriverRegistration] = useMutation(UPDATE_DRIVER_REGISTRATION);
    return { updateDriverRegistration };
};

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

// Driver logout, on the centralized asset-driver route (IC-798 / IC-961). Unlike the
// legacy route it permits the registered driver *or* an admin, which is what makes the
// admin "check and log out driver" flow work at all. Denials arrive as
// extensions.code B1012 — see driverActionErrorMessage in ConfigBasic.
const DEREGISTER_ASSET_DRIVER = gql`
    mutation deregisterAssetDriver($idAsset: ID!) {
        deregisterAssetDriver(idAsset: $idAsset) {
            idContactPerson
            start
            end
        }
    }
`;

export const useDeregisterAssetDriver = () => {
    const [deregisterAssetDriver] = useMutation(DEREGISTER_ASSET_DRIVER);
    return { deregisterAssetDriver };
};

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const UPDATE_LOGBOOK_PRIVATE_JOURNEY = gql`
    mutation updateLogbookPrivateJourney($idLogbook: ID!, $privateJourneyOn: Boolean!) {
        updateLogbookPrivateJourney(id: $idLogbook, privateJourneyOn: $privateJourneyOn) {
            idPrivateJourneyPerson
        }
    }
`;

export const useUpdateLogbookPrivateJourney = () => {
    const [updateLogbookPrivateJourney] = useMutation(UPDATE_LOGBOOK_PRIVATE_JOURNEY, {
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
    });
    return { updateLogbookPrivateJourney };
};

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const UPDATE_LOGBOOK_ARCHIVE_STATUS = gql`
    mutation updateLogbookArchiveStatus($idLogbook: ID!, $status: EnumLogbookStatus!, $access: [AccessInput]) {
        updateLogbook(input: { status: $status, access: $access }, id: $idLogbook) {
            id
        }
    }
`;

export const useUpdateLogbookArchiveStatus = () => {
    const [updateLogbookArchiveStatus] = useMutation(UPDATE_LOGBOOK_ARCHIVE_STATUS);
    return { updateLogbookArchiveStatus };
};

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const BULK_UPDATE_LOGBOOK_AUTOFILL = gql`
    mutation bulkUpdateLogbookAutofill($idLogbooks: [String]!, $autofillActivated: Boolean!) {
        bulkUpdateLogbookAutofill(idLogbooks: $idLogbooks, autofillActivated: $autofillActivated) {
            id
            name
            autofillActivated
        }
    }
`;
export const useBulkUpdateLogbookAutofill = () => {
    const [bulkUpdateLogbookAutofill, { loading }] = useMutation(BULK_UPDATE_LOGBOOK_AUTOFILL, {
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
    });
    return { bulkUpdateLogbookAutofill, loading };
};

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const UPDATE_LOGBOOK_ASSIGNED_DRIVER = gql`
    mutation updateLogbookAssignedDriver($id: ID!, $idAssignedDriver: ID) {
        updateLogbookAssignedDriver(id: $id, idAssignedDriver: $idAssignedDriver) {
            id
            idAssignedDriver
            assignedDriver {
                id
                firstname
                lastname
            }
        }
    }
`;

export const useUpdateLogbookAssignedDriver = () => {
    const [updateLogbookAssignedDriver, { loading }] = useMutation(UPDATE_LOGBOOK_ASSIGNED_DRIVER, {
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
    });
    return { updateLogbookAssignedDriver, loading };
};

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_ASSET_CALIBRATION_DATA = gql`
    query getAssetCalibrationData($id: ID!, $date: Date!) {
        assetOdom(id: $id, date: $date) {
            calibration {
                dtCalibration
                calibrationKm
                km
                ymd
            }
            latest {
                km
                ymd
            }
        }
    }
`;

/**
 * Returns the latest user calibration data from mileage
 * @param {String} idAsset, id from the asset
 * @param {Function} setMessage, a function to set an error message
 * @returns latest mileage calibration data
 */
export function useGetLatestUserCalibrationData(idAsset, setMessage) {
    const currentDate = Moment().format('YYYY-MM-DD');
    const [calibrationData, setCalibrationData] = useState(undefined);

    useQuery(GET_ASSET_CALIBRATION_DATA, {
        variables: {
            id: idAsset,
            date: currentDate,
        },
        skip: !idAsset,
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
        onCompleted: (data) => {
            setCalibrationData({
                dtCalibration: data?.assetOdom?.calibration ? data?.assetOdom?.calibration?.dtCalibration : null,
                calibrationKm: data?.assetOdom?.calibration ? data?.assetOdom?.calibration?.calibrationKm : null,
                km: data?.assetOdom?.calibration ? data?.assetOdom?.calibration?.km : null,
                ymd: data?.assetOdom?.calibration ? data?.assetOdom?.calibration?.ymd : null,
                latestKm: data?.assetOdom?.latest ? data?.assetOdom?.latest?.km : null,
                latestYmd: data?.assetOdom?.latest ? data?.assetOdom?.latest?.ymd : null,
            });
        },
        onError: (error) => {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
            setCalibrationData({
                dtCalibration: null,
                calibrationKm: null,
                km: null,
                ymd: null,
                latestKm: null,
                latestYmd: null,
            });
        },
    });
    if (calibrationData !== undefined) {
        return calibrationData;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

export function useGetLatestMileage(idAsset, setMessage) {
    const calibrationData = useGetLatestUserCalibrationData(idAsset, setMessage);

    if (calibrationData?.dtCalibration && moment(calibrationData?.dtCalibration).isAfter(calibrationData?.latestYmd)) {
        return {
            latestMileage: getNumberWithThousandSeparator(calibrationData?.calibrationKm, 0),
        };
    } else if (!calibrationData?.dtCalibration && !calibrationData?.latestYmd) {
        return {
            latestMileage: getNumberWithThousandSeparator(0, 0),
        };
    } else {
        return {
            latestMileage: getNumberWithThousandSeparator(calibrationData?.latestKm, 0),
        };
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_ALL_LOGBOOKS = gql`
    query getAllLogbooks($filterInput: [FilterSpecInput]) {
        logbooks(filter: { op: AND, filters: $filterInput }) {
            name
            id
            status
            idAsset
            idDrivers
            asset {
                privateJourney {
                    end
                    start
                    idPerson
                }
                child {
                    ... on VehicleChild {
                        licensePlate
                        vin
                    }
                }
            }
            access {
                holder
                allowed
                variant
                dtNotAfter
                dtNotBefore
            }
            autofillActivated
            idAssignedDriver
            assignedDriver {
                id
                firstname
                lastname
            }
        }
    }
`;

const createDynamicFilter = (filter) => {
    const filterSpecInput = [];
    filter.forEach((filter) => {
        if (filter.name === 'Status' && filter.value === 'all') {
            filterSpecInput.push({
                val: filter.value.toLocaleLowerCase(),
                op: 'NE',
                key: filter.name.toLocaleLowerCase(),
            });
        } else if (filter.name === 'Status') {
            filterSpecInput.push({
                val: filter.value.toLocaleLowerCase(),
                op: 'EQ',
                key: filter.name.toLocaleLowerCase(),
            });
        } else if (filter.name === 'Driver') {
            filterSpecInput.push({
                val: filter.value.toLocaleLowerCase(),
                op: 'EQ',
                key: 'idDrivers',
            });
        }
    });
    return filterSpecInput;
};

const mapAllLogbooksResponse = (logbooks) => {
    if (!logbooks?.length) {
        return [];
    }

    return logbooks.map((logbook) => ({
        id: logbook?.id,
        name: logbook?.name,
        status: logbook?.status,
        idAsset: logbook?.idAsset,
        autofillActivated: logbook?.autofillActivated,
        access: logbook?.access,
        asset: logbook?.asset,
        idDrivers: logbook?.idDrivers,
        idAssignedDriver: logbook?.idAssignedDriver ?? null,
        assignedDriver: logbook?.assignedDriver ?? null,
    }));
};

export function useGetAllLogbooksApi(setMessage, filter = [{ name: 'Status', value: 'all' }]) {
    let [logbookData, setLogbookData] = useState(undefined);

    const { data, error } = useQuery(GET_ALL_LOGBOOKS, {
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
        variables: {
            filterInput: createDynamicFilter(filter),
        },
    });

    useEffect(() => {
        if (data) {
            setLogbookData(mapAllLogbooksResponse(data.logbooks));
        }
    }, [data]);

    useEffect(() => {
        if (error) {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
            setLogbookData([]);
        }
    }, [error, setMessage]);

    if (logbookData !== undefined) {
        return logbookData;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//
export function useGetLogbooksDrivers(selectedLogbooks, setMessage, filter) {
    const [selectedLogbooksDrivers, setSelectedLogbooksDrivers] = useState({});

    useQuery(GET_ALL_LOGBOOKS, {
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
        variables: {
            filterInput: createDynamicFilter(filter),
        },
        onCompleted: (data) => {
            const filteredLogbooks = data.logbooks.filter((logbook) => selectedLogbooks.includes(logbook.id));
            const idDriversByLogbook = {};
            filteredLogbooks.forEach((logbook) => {
                const drivers = logbook.idDrivers;
                idDriversByLogbook[logbook.id] = drivers;
            });
            setSelectedLogbooksDrivers(idDriversByLogbook);
        },
        onError: (error) => {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
        },
    });

    return selectedLogbooksDrivers;
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//
export function useGetLogbooksAccess(selectedLogbooks, setMessage, filter) {
    const [selectedLogbooksAccess, setSelectedLogbooksAccess] = useState([]);

    useQuery(GET_ALL_LOGBOOKS, {
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
        variables: {
            filterInput: createDynamicFilter(filter),
        },
        onCompleted: (data) => {
            const filteredLogbooks = data.logbooks.filter((logbook) => selectedLogbooks.includes(logbook.id));
            const logbooksAccess = [];
            filteredLogbooks.forEach((logbook) => {
                logbooksAccess.push({
                    idLogbook: logbook.id,
                    access: logbook.access,
                });
            });
            setSelectedLogbooksAccess(logbooksAccess);
        },
        onError: (error) => {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
        },
    });

    return selectedLogbooksAccess;
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

export function useGetSelectedLogbooksReasons(logbookIds, setCurrentReasons) {
    const currentValues = [];

    const client = useApolloClient();
    const promises = logbookIds.map((id) => {
        return client.query({
            query: GET_SELECTED_LOGBOOK_REASONS,
            variables: {
                id: id,
            },
        });
    });

    const getReasons = async () => {
        try {
            const results = await Promise.all(promises);

            results.forEach((result) => {
                const newReasons = result?.data?.logbooks[0]?.reasons.map((reason) => {
                    return {
                        label: reason.label,
                        idPerson: reason.idPerson,
                    };
                });
                currentValues.push({
                    idLogbook: result?.data?.logbooks[0]?.id,
                    reasons: newReasons,
                });
            });

            if (currentValues.length > 0) {
                setCurrentReasons(currentValues);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        getReasons();
        // eslint-disable-next-line
    }, []);
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const IMPORT_LOGBOOK_REASON = gql`
    mutation importLogbookReason($id: ID!, $reasons: [LogbookReasonInput]) {
        updateLogbook(input: { reasons: $reasons }, id: $id) {
            id
        }
    }
`;

export function useImportLogbookReasonApi() {
    const { t } = useTranslation();
    const client = useApolloClient();

    const saveReasons = async (newCurrentReasons, setMessage) => {
        const promises = newCurrentReasons.map((cR) => {
            return client.mutate({
                mutation: IMPORT_LOGBOOK_REASON,
                context: {
                    headers: {
                        'x-customer-super': 'on',
                    },
                },
                variables: {
                    id: cR.idLogbook,
                    reasons: cR.reasons,
                },
            });
        });

        try {
            await Promise.all(promises);
            setMessage(t('reasonsImported'));
        } catch (error) {
            console.log(error);
            setMessage(t('errorDuringImport'));
        }
    };

    return { saveReasons };
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const ASSETS_PAGE_SIZE = 1000;
const ASSETS_MAX_PAGE_COUNT = 50;

const GET_ALL_ASSETS = gql`
    query getAllAssets($page: PageSpecInput) {
        assets(page: $page, sort: [{ key: "id", op: ASC }], filter: { val: null, op: NE, key: "child" }) {
            id
            name
            icon
            child {
                __typename
                ... on VehicleChild {
                    licensePlate
                    vin
                    model
                    imageUri
                    subtype
                }
            }
        }
    }
`;

const isAbortError = (error) => error?.name === 'AbortError' || error?.networkError?.name === 'AbortError';

export function useGetAllAssets(listOfAssetIds, assetsData) {
    const [assetsWithOutLogbook, setAssetsWithOutLogbook] = useState([]);
    const client = useApolloClient();
    const latestArgsRef = useRef({ listOfAssetIds, assetsData });
    const requestIdRef = useRef(0);
    const abortRef = useRef(null);
    const inFlightRef = useRef(null);

    // Keep the latest arguments without changing the identity of getAllAssets (it is used in useEffect dependencies)
    useEffect(() => {
        latestArgsRef.current = { listOfAssetIds, assetsData };
    });

    // Abort an in-flight request when the component unmounts
    useEffect(
        () => () => {
            requestIdRef.current += 1;
            abortRef.current?.abort();
        },
        []
    );

    const fetchAllAssets = useCallback(async () => {
        const requestId = ++requestIdRef.current;
        const isStale = () => requestId !== requestIdRef.current;

        const controller = new AbortController();
        abortRef.current = controller;

        const fetchPage = async (page) => {
            const { data } = await client.query({
                query: GET_ALL_ASSETS,
                variables: { page },
                context: { fetchOptions: { signal: controller.signal } },
                fetchPolicy: 'no-cache',
            });

            return data?.assets ?? [];
        };

        try {
            const assetsById = new Map();
            let unstablePagination = false;

            for (let page = 1; page <= ASSETS_MAX_PAGE_COUNT; page++) {
                const batch = await fetchPage({ page, pageSize: ASSETS_PAGE_SIZE });
                if (isStale()) return;

                if (batch.length === 0) {
                    break;
                }

                const previousSize = assetsById.size;
                batch.forEach((asset) => assetsById.set(asset.id, asset));

                // Duplicate IDs across pages mean the backend paginates over an unstable order
                if (assetsById.size - previousSize < batch.length) {
                    unstablePagination = true;
                    break;
                }

                if (batch.length < ASSETS_PAGE_SIZE) {
                    break;
                }
            }

            if (unstablePagination) {
                assetsById.clear();
                const allAssets = await fetchPage(null);
                if (isStale()) return;

                allAssets.forEach((asset) => assetsById.set(asset.id, asset));
            }

            const { listOfAssetIds: currentAssetIds, assetsData: currentAssetsData } = latestArgsRef.current;
            const allAssets = Array.from(assetsById.values());

            // Filter assets based on the list of asset IDs
            const filteredAssets = allAssets.filter((asset) => (currentAssetIds ?? []).includes(asset.id));

            // Get VehicleChilds that are not included in filteredAssets
            const newVehiclesAssets = allAssets.filter(
                (asset) => asset.child?.__typename === 'VehicleChild' && !filteredAssets.some((filteredAsset) => filteredAsset.id === asset.id)
            );

            // Assign the filtered assets to assetsData.current
            currentAssetsData.current = filteredAssets;

            // Assign the new filtered VehicleChilds to allVehiclesAssets state
            setAssetsWithOutLogbook(newVehiclesAssets);
        } catch (error) {
            if (isAbortError(error) || isStale()) return;
            console.log(error);
        }
    }, [client]);

    // Coalesce concurrent calls: callers share the in-flight request instead of aborting each other
    const getAllAssets = useCallback(() => {
        if (inFlightRef.current) {
            return inFlightRef.current;
        }

        inFlightRef.current = fetchAllAssets().finally(() => {
            inFlightRef.current = null;
        });

        return inFlightRef.current;
    }, [fetchAllAssets]);

    return { getAllAssets, assetsWithOutLogbook };
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

export const SAVE_LOGBOOK_REASONS = gql`
    mutation saveLogbookReasons($id: ID!, $reasons: [LogbookReasonInput]) {
        updateLogbook(input: { reasons: $reasons }, id: $id) {
            id
        }
    }
`;

export const useSaveLogbookReasons = (savingResults) => {
    const [saveReasons] = useMutation(SAVE_LOGBOOK_REASONS, {
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
        onCompleted: () => {
            const previousResults = savingResults.current;
            previousResults.push('Success');
            savingResults.current = previousResults;
        },
        onError: (error) => {
            console.log(error);
            const previousResults = savingResults.current;
            previousResults.push('Error');
            savingResults.current = previousResults;
        },
    });

    return { saveReasons };
};

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

/**
 * Adds a single reason to a logbook from the trip editing screen.
 * The complete reason list is read with fetchPolicy "network-only" right before
 * writing, because updateLogbook replaces the whole reasons array. Writing a
 * filtered or locally kept list would delete other users' private reasons.
 *
 * @param {Object} params
 * @param {String} params.logbookId, id of the logbook that is being edited
 * @param {String} params.currentUserId, id of the logged in person
 * @param {Function} params.setMessage, function to set a message for the snackbar
 * @returns {Object} { addReason, loading }
 */
export function useAddLogbookReason({ logbookId, currentUserId, setMessage }) {
    const client = useApolloClient();
    const { t } = useTranslation();
    const [saveReasons, { loading }] = useMutation(SAVE_LOGBOOK_REASONS);

    const addReason = useCallback(
        async (label, sharedWithAll) => {
            const trimmedLabel = (label ?? '').trim();

            if (trimmedLabel.length === 0 || (!sharedWithAll && !currentUserId)) {
                return { status: 'invalid' };
            }

            try {
                const { data } = await client.query({
                    query: GET_SELECTED_LOGBOOK_REASONS,
                    variables: {
                        id: logbookId,
                    },
                    fetchPolicy: 'network-only',
                });

                const existingReasons = (data?.logbooks?.[0]?.reasons ?? [])
                    .filter((reason) => reason?.label)
                    .map((reason) => {
                        return {
                            label: reason.label,
                            idPerson: reason.idPerson ?? null,
                        };
                    });

                const idPerson = sharedWithAll ? null : currentUserId;
                const duplicateReason = existingReasons.find((reason) => reason.label === trimmedLabel && reason.idPerson === idPerson);

                if (duplicateReason !== undefined) {
                    setMessage(t('reasonAlreadyExists'));
                    return { status: 'duplicate', reason: duplicateReason };
                }

                const newReason = {
                    label: trimmedLabel,
                    idPerson: idPerson,
                };

                await saveReasons({
                    variables: {
                        id: logbookId,
                        reasons: existingReasons.concat(newReason),
                    },
                });

                //Reload the reasons of the open screens, without blocking the current form
                client.refetchQueries({
                    include: ['getSelectedLogbookReasons'],
                });
                setMessage(t('reasonCreated'));

                return { status: 'created', reason: newReason };
            } catch (error) {
                setMessage(error?.graphQLErrors?.[0]?.message ?? error?.networkError?.message ?? '');
                return { status: 'error' };
            }
        },
        [client, currentUserId, logbookId, saveReasons, setMessage, t]
    );

    return { addReason, loading };
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const SAVE_LOGBOOK_DRIVERS = gql`
    mutation saveLogbookDrivers($id: ID!, $idDrivers: [String]!) {
        updateLogbook(input: { idDrivers: $idDrivers }, id: $id) {
            id
        }
    }
`;

export const useSaveLogbookDrivers = () => {
    const { t } = useTranslation();
    const client = useApolloClient();
    const [saveLogbookDrivers, { loading, error }] = useMutation(SAVE_LOGBOOK_DRIVERS);

    const handleSaveLogbookDrivers = async (logbookData, setMessage) => {
        try {
            const updatedLogbooks = await Promise.all(
                Object.entries(logbookData).map(async ([id, idDrivers]) => {
                    const response = await saveLogbookDrivers({
                        variables: { id, idDrivers },
                        context: {
                            headers: {
                                'x-customer-super': 'on',
                            },
                        },
                    });
                    return response.data.updateLogbook;
                })
            );
            setMessage(`${Object.keys(logbookData).length} ${t('logbooksUpdated')}`);
            client.refetchQueries({
                include: ['getAllLogbooks'],
            });
            return updatedLogbooks;
        } catch (errors) {
            setMessage(errors.message);
        }
    };

    return { handleSaveLogbookDrivers, loading, error };
};

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const UPDATE_LOGBOOK_SETTING = gql`
    mutation updateLogbookSetting($id: ID!, $logbookSetting: LogbookSettingInput!) {
        updateCustomerSetting(id: $id, input: { logbookSetting: $logbookSetting }) {
            id
        }
    }
`;

export const useUpdateLogbookSetting = (setMessage) => {
    const { t } = useTranslation();

    const [updateLogbookSetting] = useMutation(UPDATE_LOGBOOK_SETTING, {
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
        refetchQueries: [{ query: GET_LOGBOOK_SETTING }],
        onCompleted: () => {
            setMessage(t('updateSuccessful'));
        },
        onError: (error) => {
            setMessage(t('updateError'));
            console.log(error?.graphQLErrors?.[0]?.message ?? error?.message);
        },
    });

    return { updateLogbookSetting };
};

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_LOGBOOK_SETTING = gql`
    query getLogbookSetting {
        customerSettings {
            logbookSetting {
                key
                value
            }
            id
        }
    }
`;

export function useGetLogbookSetting(setMessage) {
    const { t } = useTranslation();
    const [logbookSetting, setLogbookSetting] = useState({
        id: undefined,
        map: undefined,
    });

    useQuery(GET_LOGBOOK_SETTING, {
        onCompleted: (data) => {
            const newSettingMap = new Map();
            const listOfSetting = data?.customerSettings[0]?.logbookSetting?.value;
            listOfSetting?.forEach((cost) => {
                newSettingMap.set(cost.key, cost.value);
            });
            setLogbookSetting({
                id: data?.customerSettings[0]?.id,
                map: newSettingMap,
            });
        },
        onError: (error) => {
            console.log(error?.graphQLErrors?.[0]?.message ?? error?.message);
            setMessage(t('dataRetrievalFailed'));
            setLogbookSetting({
                id: null,
                map: null,
            });
        },
    });

    if (logbookSetting.id !== undefined) {
        return logbookSetting;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_SELECTED_LOGBOOK_NOTIFICATION = gql`
    query getSelectedLogbooksNotifications {
        logbooks {
            id
            notifications {
                receivers
                topic
            }
        }
    }
`;
//-------------  teams access-----------------------//
//--------------------------------------------------//

const GET_TEAM_ACCESS = gql`
    query getTeam {
        teams(filter: { key: "license", val: "user", op: EQ }) {
            id
        }
    }
`;

/**
 * Returns a list of logbooks with their notifications
 * @param {Array} logbookIds - list of logbook IDs
 * @param {Function} setMessage - function to set an error message
 * @returns {Array} list of logbooks with their notifications
 */
export function useLogbookNotifications(logbookIds, setMessage) {
    const [notificationEmails, setNotificationEmails] = useState([]);

    useQuery(GET_SELECTED_LOGBOOK_NOTIFICATION, {
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
        onCompleted: (data) => {
            const filteredLogbooks = data.logbooks.filter((logbook) => logbookIds.includes(logbook.id));

            const notifications = filteredLogbooks.map((logbook) => {
                return {
                    logbookId: logbook.id,
                    notifications: logbook.notifications || [],
                };
            });

            setNotificationEmails(notifications);
        },
        onError: (error) => {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
        },
    });

    return notificationEmails;
}

const UPDATE_LOGBOOK_NOTIFICATIONS = gql`
    mutation updateLogbookNotifications($id: ID!, $notifications: [LogbookNotificationsInput]) {
        updateLogbook(input: { notifications: $notifications }, id: $id) {
            id
        }
    }
`;

/**
 * Returns a function to update logbook notifications
 * @param {Function} setMessage - function to set an error message
 * @returns {Function} to update logbook notifications
 */

export function useUpdateLogbookNotifications(setMessage) {
    const [updateLogbook] = useMutation(UPDATE_LOGBOOK_NOTIFICATIONS, {
        refetchQueries: ['getSelectedLogbook'],
    });

    const updateNotifications = async (id, notifications) => {
        try {
            const { data } = await updateLogbook({
                context: {
                    headers: {
                        'x-customer-super': 'on',
                    },
                },
                variables: {
                    id,
                    notifications: notifications,
                },
            });
            return data;
        } catch (error) {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
        }
    };

    const updateNotificationsForIds = async (ids, notifications) => {
        const results = [];
        for (const id of ids) {
            try {
                const result = await updateNotifications(id, notifications);
                results.push(result);
            } catch (error) {
                setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
            }
        }
        return results;
    };

    return updateNotificationsForIds;
}

export const useGetTeam = () => {
    const { data: teams } = useQuery(GET_TEAM_ACCESS);

    if (teams !== undefined && teams.length !== 0) {
        return teams.teams[0].id;
    }
};

const SAVE_LOGBOOK_ACCESS = gql`
    mutation saveLogbookAccess($id: ID!, $access: [AccessInput]!) {
        updateLogbook(input: { access: $access }, id: $id) {
            id
        }
    }
`;

export const useLogbookAccessUpdater = (setMessage, t) => {
    const client = useApolloClient();
    const [loading, setLoading] = useState(false);

    const updateAccessForAllLogbooks = async (logbookAccessObject) => {
        setLoading(true);
        const promises = logbookAccessObject.map((obj) => {
            return client.mutate({
                mutation: SAVE_LOGBOOK_ACCESS,
                variables: {
                    id: obj.idLogbook,
                    access: obj.access,
                },
                context: {
                    headers: {
                        'x-customer-super': 'on',
                    },
                },
                refetchQueries: ['getLogbooks'],
            });
        });

        try {
            await Promise.all(promises);
            setMessage(t('accesssChangedSuccessfully'));
            setLoading(false);
            return true;
        } catch (error) {
            console.log(error);
            setMessage('accessChangedError');
            setLoading(false);
            return false;
        }
    };

    return { updateAccessForAllLogbooks, loading };
};

export const useUpdateArchivedLogbookAccess = (setMessage) => {
    const { t } = useTranslation();

    const [updateArchivedLogbookAccess] = useMutation(SAVE_LOGBOOK_ACCESS, {
        context: {
            headers: {
                'x-customer-super': 'on',
            },
        },
        refetchQueries: ['getLogbooks'],
        onError: (error) => {
            setMessage(t('accessChangedError'));
            console.log(error?.graphQLErrors?.[0]?.message ?? error?.message);
        },
    });

    return { updateArchivedLogbookAccess };
};

const TEAM_ID = gql`
    query getTeamId {
        teams(filter: { val: "user", op: EQ, key: "name" }) {
            id
        }
    }
`;

export const useGetTeamId = () => {
    const { data, loading, error } = useQuery(TEAM_ID);

    if (loading) {
        return { loading: true };
    }

    if (error) {
        return { error };
    }

    const teamID = data?.teams?.[0]?.id;

    if (teamID) {
        return { teamID };
    }

    return { teamID: null };
};

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//

const GET_TOURS_WITH_COST = gql`
    query getToursWithCost($firstDayOfMonth: Object, $lastDayOfMonth: Object) {
        logbooktours(
            filter: {
                op: OR
                filters: [
                    {
                        op: AND
                        filters: [
                            { val: "EDITED", op: EQ, key: "status" }
                            { val: $firstDayOfMonth, op: GTE, key: "dtBegin" }
                            { val: $lastDayOfMonth, op: LTE, key: "dtBegin" }
                            { val: null, op: NE, key: "costCentre" }
                        ]
                    }
                    {
                        op: AND
                        filters: [
                            { val: "EDITED", op: EQ, key: "status" }
                            { val: $firstDayOfMonth, op: GTE, key: "dtBegin" }
                            { val: $lastDayOfMonth, op: LTE, key: "dtBegin" }
                            { val: null, op: NE, key: "costObject" }
                        ]
                    }
                    {
                        op: AND
                        filters: [
                            { val: "EXPORTED", op: EQ, key: "status" }
                            { val: $firstDayOfMonth, op: GTE, key: "dtBegin" }
                            { val: $lastDayOfMonth, op: LTE, key: "dtBegin" }
                            { val: null, op: NE, key: "costCentre" }
                        ]
                    }
                    {
                        op: AND
                        filters: [
                            { val: "EXPORTED", op: EQ, key: "status" }
                            { val: $firstDayOfMonth, op: GTE, key: "dtBegin" }
                            { val: $lastDayOfMonth, op: LTE, key: "dtBegin" }
                            { val: null, op: NE, key: "costObject" }
                        ]
                    }
                ]
            }
        ) {
            dtBegin
            idLogbook
            costCentre
            costObject
            distance
            type
        }
    }
`;

export function useGetToursWithCost(date, setMessage) {
    const firstDayOfMonth = Moment(date).startOf('month');
    const lastDayOfMonth = Moment(date).endOf('month');
    const [toursWithCost, setToursWithCost] = useState(undefined);

    useQuery(GET_TOURS_WITH_COST, {
        variables: {
            firstDayOfMonth,
            lastDayOfMonth,
        },
        onCompleted: (data) => {
            setToursWithCost(data.logbooktours);
        },
        onError: (error) => {
            setMessage(error?.graphQLErrors?.[0]?.message ?? error?.message);
            setToursWithCost([]);
        },
    });

    if (toursWithCost !== undefined) {
        return toursWithCost;
    }
}

//--------------------------------------------------//
//--------------------------------------------------//
//--------------------------------------------------//
